import useScopedLoad from './hooks/useScopedLoad';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { listScheduleAuditLogs } from './api/tumblerApi';
import { formatBangkokDateTime } from './utils/bangkokTime';

// IMPLEMENTATION_PLAN.md Phase 5 — "Revision lineage ใช้ reoptimized_from/change_summary; audit
// list มาจาก endpoint ที่มี" and ASSESSMENT.md: "เพิ่ม backend scenario audit events ก่อนอ้างว่า
// history ครบทุก approve/publish/cancel" — the audit endpoint may not yet emit every lifecycle
// transition, so we show whatever it returns without claiming this is the complete history.
export default function RevisionHistory({ plant, factory }) {
  const accessToken = useSelector((state) => state.auth.access_token);


  const loader = useCallback(async (signal) => {
    const response = await listScheduleAuditLogs({ plant, factory, accessToken, signal });
    return Array.isArray(response.data) ? response.data : response.data.results || [];
  }, [plant, factory, accessToken]);
  const read = useScopedLoad(`${plant}/${factory}`, loader, Boolean(accessToken && plant && factory));
  const state = { ...read, results: read.data || [] };


  if (state.loading && !state.results.length) {
    return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 160 }}><CircularProgress size={28} /></Box>;
  }
  if (state.error) return <Alert severity="error">{state.error.summary}</Alert>;

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        This list reflects only the events the audit endpoint currently emits. Not every scenario transition
        (approve/publish/cancel) is guaranteed to be recorded yet — see ASSESSMENT.md.
      </Alert>
      {!state.results.length ? (
        <Typography color="text.secondary">No recorded activity for this plant/factory.</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', '& th, & td': { p: 0.75, borderBottom: '1px solid', borderColor: 'divider' }, '& th': { textAlign: 'left', fontWeight: 600 } }}>
            <thead>
              <tr>{['When (Bangkok)', 'Scenario', 'Event', 'Actor', 'Detail'].map((label) => <th key={label}>{label}</th>)}</tr>
            </thead>
            <tbody>
              {state.results.map((entry, index) => (
                <tr key={entry.id ?? index}>
                  <td>{formatBangkokDateTime(entry.performed_at)}</td>
                  <td>{entry.scenario ? `#${entry.scenario}` : '—'}</td>
                  <td>{entry.event || entry.action || '—'}</td>
                  <td>{entry.performed_by || '—'}</td>
                  <td>{JSON.stringify(entry.changes || {})}</td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      )}
    </Box>
  );
}
